import React, { Component } from "react";
import _ from "lodash";
import styled from "styled-components";
import { observer } from "mobx-react";

import { propertyToInputType } from "./input";
import store, { updateSelected, doToSelected, addSlide } from "../canvas/store";
import { Modals, setModal } from "../layout/editor";

import { ItemTypes, starterProperties } from "../constants/elements";
import { HRPrimary, Divider, ShowWhen } from "../reusable/layout";
import * as Icons from "../reusable/icons";
import * as Colors from "../reusable/colors";
import { InputUI } from "../reusable/components";

/*eslint no-mixed-operators: "off"*/

const Categories = styled.ul`
  padding: 0 20px;
  font-size: 0.95em;
`;

const CatLabel = styled.h2`
  font-size: 1.4em;
  letter-spacing: 1.5px;
  color: ${Colors.TextHeader};
  cursor: default;
`;

const CatContent = styled.div`
  padding-left: 0px;
`;

const PropertyRow = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: ${p => (p.newLine ? "column" : "row")};
  align-items: ${p => !p.newLine && "center"};
  &:hover .link-button {
    opacity: 1;
  }
`;

const PropertyLabel = styled.label`
  font-size: 1.1em;
`;

const CaretUI = styled.span`
  font-size: 0.5em;
  width: 10px;
  display: inline-block;
`;

const LinkButton = styled.span`
  font-size: 12px;
  margin-left: 7px;
  padding: 6px 5px 3px 5px;
  width: 10px;
  height: 10px;
  line-height: 10px;
  border-radius: 4px;
  border: 1px solid ${Colors.darkenSecondary(0.6)};
  cursor: pointer;
  &:hover {
    background-color: ${Colors.darkenSecondary(0.4)};
  }
  opacity: 0;
`;

const Action = styled.button`
  padding: 5px 8px;
  background-color: ${Colors.primary};
  text-transform: capitalize;
  border: none;
  box-shadow: 0px 1px 5px ${Colors.shadow};
  cursor: pointer;
`;

const openLinkModal = p => e => setModal(Modals.LinkSettings, { property: p });

const Caret = ({ exp }) => <CaretUI>{exp ? <Icons.CaretDown /> : <Icons.CaretRight />} </CaretUI>;

class Category extends Component {
  state = {
    expanded: ["meta", "typography", "media", "container"].includes(this.props.cat)
  };

  shouldComponentUpdate(props, state) {
    return !_.isEqual(this.props.values, props.values) || this.state.expanded !== state.expanded;
  }

  toggleExpand = () => this.setState({ expanded: !this.state.expanded });

  render() {
    const { cat } = this.props;
    const expanded = this.state.expanded;
    return (
      <li>
        <HRPrimary width="100%" />
        <Divider h="10px" />
        <CatLabel onClick={this.toggleExpand}>
          <Caret exp={expanded} /> {cat}.
        </CatLabel>
        <ShowWhen pred={expanded}>
          <Divider h="5px" />
          <CatContent>
            <ul>{this.props.children}</ul>
          </CatContent>
        </ShowWhen>
        <Divider h="10px" />
      </li>
    );
  }
}

const categories = {
  meta: ["label"],
  slider: ["slide", "idleDuration", "slidingDuration", "transition", "addSlide"],
  media: ["url", "lockAspectRatio", "resetAspectRatio", "loop"],
  resolution: ["streamWidth", "streamHeight"],
  container: ["hideOverflow"],
  "progress bar": ["progress", "maximum", "orientation"],
  typography: [
    "text",
    "fontFamily",
    "color",
    "letterSpacing",
    "lineHeight",
    "fontSize",
    "textIndent",
    "fontWeight",
    "fontStyle",
    "textAlign",
    "icon",
    "textOverflow"
  ],
  box: [
    "backgroundColor",
    //"backgroundColor2",
    "borderColor",
    "borderWidth",
    "borderStyle",
    "backgroundImage",
    "backgroundSize",
    "borderRadius",
    "borderTopLeftRadius",
    "borderTopRightRadius",
    "borderBottomLeftRadius",
    "borderBottomRightRadius",
    "gradient"
  ],
  "position & size": ["width", "height", "top", "left", "padding", "zIndex"],
  effect: [
    "opacity",
    "blur",
    "perspective",
    "rotate",
    "rotateX",
    "rotateY",
    "skewX",
    "skewY",
    "transformOrigin"
  ],
  animation: ["enterAnimation", "leaveAnimation", "delay", "childDelay"],
  collection: ["collection", "space", "direction", "limit", "skip", "cycle"],
  triggers: ["showWhen", "showEvery", "stayDuration"]
};

const propLabelOverride = {
  borderTopLeftRadius: "topLeftRadius",
  borderTopRightRadius: "topRightRadius",
  borderBottomLeftRadius: "bottomLeftRadius",
  borderBottomRightRadius: "bottomRightRadius"
};

const canvasIncludeList = [
  "streamWidth",
  "streamHeight",
  "label",
  "backgroundColor",
  "borderColor",
  "borderWidth",
  "borderStyle",
  "backgroundImage",
  "backgroundSize",
  "enterAnimation",
  "leaveAnimation"
];

const customProps = {
  [ItemTypes.ELEMENT_SLIDER]: ["addSlide"],
  [ItemTypes.ELEMENT_IMAGE]: ["resetAspectRatio"]
};

const customHandler = {
  addSlide: doToSelected(addSlide),
  resetAspectRatio: () => console.log("todo")
};

const propsInCategory = (cat, type, id) =>
  categories[cat]
    .filter(prop =>
      Object.keys(starterProperties(type))
        .concat(customProps[type])
        .concat(["label"])
        .concat(id === "canvas" && ["streamWidth", "streamHeight"])
        .includes(prop)
    )
    .filter(prop => !(id === "canvas" && !canvasIncludeList.includes(prop)));

const selectedBlockCategories = (type, id) =>
  Object.keys(categories).filter(c => propsInCategory(c, type, id).length > 0);

const renderInput = (prop, large) => {
  const Input = propertyToInputType(prop);
  return Input ? (
    <Input
      key={prop}
      large={large}
      prop={prop}
      value={store.selectedBlock[prop]}
      allProps={store.selectedBlock}
      onChange={v => updateSelected({ [prop]: v })}
      onClick={customHandler[prop]}
    />
  ) : (
    <span />
  );
};

const newLine = p => ["text", "url"].includes(p);

const renderProperty = p => (
  <li key={p}>
    <Divider h="10px" />
    {customHandler[p] ? (
      <Action onClick={() => customHandler[p]()}>{p}</Action>
    ) : (
      <PropertyRow newLine={newLine(p)}>
        <PropertyLabel htmlFor="">
          {propLabelOverride[p] || p}
          <LinkButton className="link-button" onClick={openLinkModal(p)}>
            <Icons.Link />
          </LinkButton>
        </PropertyLabel>
        {newLine(p) && <Divider h="10px" />}
        {store.selectedBlock[p] && store.selectedBlock[p].target ? (
          <InputUI
            title={store.selectedBlock[p].target}
            value={store.selectedBlock[p].target}
            disabled
          />
        ) : (
          renderInput(p, newLine(p))
        )}
      </PropertyRow>
    )}
  </li>
);

export default observer(p => {
  return (
    <div className="properties">
      <Categories>
        {selectedBlockCategories(store.selectedBlock.type, store.selectedBlock.id).map(c => {
          return (
            <Category
              key={c}
              cat={c}
              values={_.pick(
                store.selectedBlock,
                propsInCategory(c, store.selectedBlock.type, store.selectedBlock.id)
              )}>
              {propsInCategory(c, store.selectedBlock.type, store.selectedBlock.id).map(p => {
                return renderProperty(p);
              })}
            </Category>
          );
        })}
      </Categories>
    </div>
  );
});



// WEBPACK FOOTER //
// ./src/properties/index.js